/**
 * Greg James Website - Main Entry Point
 *
 * This file orchestrates the initialization of all modules.
 * Individual functionality is split into separate component files.
 */

import Swiper from 'swiper';
import game from './components/game.js';
import racing from './components/racing.js';
import { getCurrentMatchState, formatOvers, teams, getLeagueState, getLeagueStats } from './components/cricket.js';
import { createCricketGame } from './components/cricket-game.js';

// Utility modules
import { isTouchDevice, detectBrowser, getClosestPastTime, formatScheduleStatus } from './components/utils.js';

// Feature modules
import {
  player,
  initPlayerState,
  bindPlayers,
  stopPlayer,
  resetPlayer,
} from './components/player.js';

import {
  getSchedule,
  initSchedule,
  updateTimelines,
  checkForActive,
  fetchGraphQLData,
  fetchScheduleRows,
} from './components/schedule.js';

import {
  bindAjaxLoad,
  setupNavigationObserver,
  loadPage,
  setupPopstateHandler,
} from './components/navigation.js';

import { initSoundboard, stopSoundBoard, getSoundBoardPlayer } from './components/soundboard.js';
import bind_video_players from './components/bind-video-players.js';

//------------------
// Global State
//------------------

window.his = window.location.pathname;
window.touch = isTouchDevice();
window.browser = detectBrowser();
window.logoShowing = false;
window.fullscreen = 0;
window.players = [];

document.body.classList.add(window.browser.replace(' ', '').toLowerCase());

const isOffline = document.querySelector('.offline-mode');

// Per-page lifecycle. Each AJAX page-load can spawn intervals/timeouts that
// the previous load left running; we track their IDs here and clear them at
// the top of pageLoad() so they don't multiply.
const pageTimers = { intervals: [], timeouts: [] };
function registerPageInterval(id) { pageTimers.intervals.push(id); return id; }
function registerPageTimeout(id) { pageTimers.timeouts.push(id); return id; }
function clearPageTimers() {
  pageTimers.intervals.forEach(clearInterval);
  pageTimers.timeouts.forEach(clearTimeout);
  pageTimers.intervals = [];
  pageTimers.timeouts = [];
}

// Cabin pageview ping. Cabin's hello.js fires an initial pageview on load,
// but since the site uses AJAX nav (pushState / replaceState / popstate), we
// fire an explicit event after every history change so Cabin sees each route.
let _lastTrackedPath = null;
function trackPageview() {
  if (typeof window === 'undefined' || !window.cabin) return;
  const path = location.pathname + location.search;
  if (path === _lastTrackedPath) return; // avoid duplicate hits
  _lastTrackedPath = path;
  try {
    if (typeof window.cabin.event === 'function') {
      window.cabin.event('pageview');
    }
  } catch (e) { /* noop */ }
}

// Patch history.pushState/replaceState so any caller in the codebase triggers
// a pageview without needing to know about Cabin. Schedule on the next tick so
// the new URL is current before we read location.pathname.
(function patchHistoryForAnalytics() {
  const fire = () => setTimeout(trackPageview, 0);
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function () {
    const r = origPush.apply(this, arguments);
    fire();
    return r;
  };
  history.replaceState = function () {
    const r = origReplace.apply(this, arguments);
    fire();
    return r;
  };
  window.addEventListener('popstate', fire);
})();

//------------------
// Initialize
//------------------

function firstLoad() {
  if (!isOffline) {
    const guideHeader = document.querySelector('.guide-header');
    if (guideHeader) {
      const headerInner = guideHeader.querySelector('.inner');
      initSchedule(headerInner);
      initPlayerState(getClosestPastTime());
      updateTimelines(player.startTime);
    }

    bindAppbar();
    var menuAutoOpened = bindMenu();
    animateLogo(menuAutoOpened);

    fetchScheduleRows(player, loadPageWrapper, bindPlayers);
    initTicker();
  }
}

function initTicker() {
  const bar = document.querySelector('.announcements-bar');
  if (!bar) return;

  const track = bar.querySelector('.announcements-track');
  if (!track || !track.children.length) return;

  // Measure one set of items
  const itemsHTML = track.innerHTML;
  const viewportWidth = window.innerWidth;

  // Clone items until track is at least 2x viewport width
  while (track.scrollWidth < viewportWidth * 2) {
    track.innerHTML += itemsHTML;
  }
  // One more copy for safety
  track.innerHTML += itemsHTML;

  // Mark clones as aria-hidden
  const origCount = track.querySelectorAll('a').length / (track.innerHTML.split(itemsHTML).length - 1);
  const allLinks = track.querySelectorAll('a');
  for (let i = Math.floor(allLinks.length / 2); i < allLinks.length; i++) {
    allLinks[i].setAttribute('aria-hidden', 'true');
    allLinks[i].tabIndex = -1;
  }

  const setWidth = track.scrollWidth / 2;
  const speed = 60; // pixels per second

  let offset = 0;
  let lastTime = 0;
  let paused = false;

  bar.addEventListener('mouseenter', function() { paused = true; });
  bar.addEventListener('mouseleave', function() { paused = false; });

  function tick(timestamp) {
    if (lastTime && !paused) {
      offset += (timestamp - lastTime) / 1000 * speed;
      if (offset >= setWidth) offset -= setWidth;
      track.style.transform = 'translateX(' + (-offset) + 'px)';
    }
    lastTime = timestamp;
    requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}

function pageLoad() {
  // Clear any timers/intervals from the previous page so they don't multiply.
  clearPageTimers();
  window.players = [];

  if (isOffline) {
    initOfflineMode();
  } else {
    initOnlineMode();
  }

  updateToggleIcon();
}

function initOfflineMode() {
  const apps = document.querySelectorAll('.app');
  const appBody = document.querySelector('.app-body');
  const close = document.querySelector('.close');

  for (let a = 0; a < apps.length; a++) {
    const app = apps[a];

    app.addEventListener('click', function (e) {
      e.preventDefault();
      appBody.classList.remove('hidden');

      setTimeout(function () {
        game();
        close.style.opacity = 1;
      }, 1000);
    });

    close.addEventListener('click', function (e) {
      e.preventDefault();
      appBody.classList.add('hidden');
      close.style.opacity = 0;
    });
  }
}

function initOnlineMode() {
  sidebarState();
  bindAjaxLoad(loadContent);
  setupNavigationObserver(loadContent);

  const schedule = getSchedule();
  if (schedule) {
    bindPlayers(schedule, loadPageWrapper);
  }

  // Update schedule info for non-live episodes
  updateScheduleInfo();

  initSoundboard();
  bind_video_players();
  game();
  racing();
  initCricket();
  initSlideshows();
}

function initSlideshows() {
  document.querySelectorAll('.slideshow.swiper').forEach(function(el) {
    new Swiper(el, {
      loop: true,
      slidesPerView: 1,
      spaceBetween: 0,
    });
  });
}

//------------------
// Cricket League - NES Style (Canvas-based)
//------------------

let cricketGame = null;
let cricketInterval = null;
let lastBallIndex = -1;

// Typewriter state for commentary
let typewriterText = '';
let typewriterTarget = '';
let typewriterIndex = 0;
let typewriterTimeout = null;
const TYPEWRITER_SPEED = 30; // ms per character

// Prediction state
let currentPrediction = null;
let predictionsCorrect = 0;
let predictionsTotal = 0;
let predictedBallIndex = -1;

// Achievement state
const ACHIEVEMENTS = {
  watched100: { id: 'watched100', name: 'Ball Watcher', desc: 'Watched 100 balls', icon: '👀' },
  watched500: { id: 'watched500', name: 'Cricket Fan', desc: 'Watched 500 balls', icon: '🏏' },
  sawSix: { id: 'sawSix', name: 'Maximum!', desc: 'Witnessed a six', icon: '6️⃣' },
  sawHatTrick: { id: 'sawHatTrick', name: 'Hat Trick Hero', desc: 'Witnessed a hat-trick', icon: '🎩' },
  predicted10: { id: 'predicted10', name: 'Fortune Teller', desc: 'Got 10 predictions right', icon: '🔮' },
  predicted50: { id: 'predicted50', name: 'Cricket Oracle', desc: 'Got 50 predictions right', icon: '🧙' },
  sawWicket: { id: 'sawWicket', name: 'Wicket Witness', desc: 'Saw a wicket fall', icon: '🎯' },
  saw10Wickets: { id: 'saw10Wickets', name: 'Wicket Collector', desc: 'Saw 10 wickets fall', icon: '🏆' },
  sawRainDelay: { id: 'sawRainDelay', name: 'Rain Stopped Play', desc: 'Experienced rain delay', icon: '🌧️' },
  sawEasterEgg: { id: 'sawEasterEgg', name: 'Eagle Eye', desc: 'Spotted a rare event', icon: '🥚' },
};

let userStats = {
  ballsWatched: 0,
  sixesWatched: 0,
  wicketsWatched: 0,
  consecutiveWickets: 0,
  predictionsCorrect: 0,
};

// Wagon wheel data - tracks where runs were scored
let wagonWheelData = [];
// Manhattan chart data - runs per over
let manhattanData = [];
// Head to head records
let headToHeadData = {};

// Stats overlay state
let statsOverlayVisible = false;
let currentStatsTab = 'wagon';

function initCricket() {
  const broadcast = document.getElementById('cricket-broadcast');
  if (!broadcast) return;

  // Close button
  const closeBtn = broadcast.querySelector('.nes-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', function() {
      if (cricketGame) {
        cricketGame.destroy();
        cricketGame = null;
      }
      if (cricketInterval) {
        clearInterval(cricketInterval);
        cricketInterval = null;
      }
      if (typewriterTimeout) {
        clearTimeout(typewriterTimeout);
        typewriterTimeout = null;
      }
      // Close app the same way the menu toggle does — fade out and remove
      var appBody = document.querySelector('.app-body');
      if (appBody) {
        appBody.style.opacity = 0;
        document.querySelectorAll('.app').forEach(function(a) { a.style.opacity = ''; });
        setTimeout(function () {
          if (appBody.parentNode) {
            appBody.parentNode.removeChild(appBody);
          }
        }, 300);
      }
      window.history.pushState({}, '', '/');
    });
  }

  // Initialize canvas game
  const canvas = document.getElementById('cricket-canvas');
  if (canvas) {
    cricketGame = createCricketGame(canvas);
  }

  // Initialize teletext panels
  updateLeagueTable();

  // Load user stats and achievements from localStorage
  loadUserStats();

  // Initialize prediction buttons
  initPredictPanel();

  // Initialize stats overlay
  initStatsOverlay();

  // Start the UI update loop (scoreboard, stats, etc.)
  lastBallIndex = -1;
  updateCricketUI();
  cricketInterval = registerPageInterval(setInterval(updateCricketUI, 1000));
}

function updateCricketUI() {
  const broadcast = document.getElementById('cricket-broadcast');
  if (!broadcast) {
    if (cricketInterval) clearInterval(cricketInterval);
    if (cricketGame) {
      cricketGame.destroy();
      cricketGame = null;
    }
    return;
  }

  const state = getCurrentMatchState();

  // Update scoreboard
  const awayAbbr = document.getElementById('nes-away-abbr');
  const homeAbbr = document.getElementById('nes-home-abbr');
  const awayScore = document.getElementById('nes-away-score');
  const homeScore = document.getElementById('nes-home-score');
  const teamAway = document.getElementById('nes-team-away');
  const teamHome = document.getElementById('nes-team-home');
  const innings = document.getElementById('nes-innings');
  const overs = document.getElementById('nes-overs');

  if (awayAbbr) {
    awayAbbr.textContent = state.awayTeam.shortName;
    awayAbbr.parentElement.style.setProperty('--team-color', state.awayTeam.color);
  }
  if (homeAbbr) {
    homeAbbr.textContent = state.homeTeam.shortName;
    homeAbbr.parentElement.style.setProperty('--team-color', state.homeTeam.color);
  }

  if (awayScore) awayScore.textContent = `${state.firstInningsScore.runs}/${state.firstInningsScore.wickets}`;
  if (homeScore) homeScore.textContent = `${state.secondInningsScore.runs}/${state.secondInningsScore.wickets}`;

  // Highlight batting team
  if (teamAway) teamAway.classList.toggle('batting', state.isFirstInnings);
  if (teamHome) teamHome.classList.toggle('batting', !state.isFirstInnings);

  // Update innings and overs
  if (innings) innings.textContent = state.isFirstInnings ? '1ST' : '2ND';
  if (overs) {
    const currentScore = state.isFirstInnings ? state.firstInningsScore : state.secondInningsScore;
    overs.textContent = formatOvers(currentScore.overs);
  }

  // Update match stats panel
  updateMatchStats(state);

  // Check if new ball for ticker/this-over update
  const currentBallIndex = state.recentBalls.length > 0 ?
    state.recentBalls[state.recentBalls.length - 1].over * 6 + state.recentBalls[state.recentBalls.length - 1].ball : -1;

  if (currentBallIndex !== lastBallIndex && state.currentBall) {
    lastBallIndex = currentBallIndex;
    updateTicker(state.currentBall);
    updateThisOver(state.recentBalls);
    // Update league table periodically
    updateLeagueTable();

    // Track ball for achievements and stats
    trackBallWatched(state.currentBall);

    // Check prediction
    checkPrediction(state.currentBall);

    // Update stats overlay if visible
    if (statsOverlayVisible) {
      drawStatsCanvas();
    }
  }
}

function updateLeagueTable() {
  const tableRows = document.getElementById('league-rows');
  if (!tableRows) return;

  // Get league state and generate standings
  const state = getLeagueState();
  const matchState = getCurrentMatchState();
  const leagueStats = getLeagueStats();

  // Season info
  const matchesPerSeason = 28; // 8 teams, each plays 7 matches
  const matchInSeason = (matchState.matchNumber % matchesPerSeason) + 1;
  const gamesLeft = matchesPerSeason - matchInSeason;

  // Update season info
  const seasonEl = document.getElementById('stat-season');
  const matchNumEl = document.getElementById('stat-match-num');
  const totalMatchesEl = document.getElementById('stat-total-matches');
  const gamesLeftEl = document.getElementById('stat-games-left');
  const leagueAgeEl = document.getElementById('stat-league-age');

  if (seasonEl) seasonEl.textContent = matchState.season;
  if (matchNumEl) matchNumEl.textContent = matchInSeason;
  if (totalMatchesEl) totalMatchesEl.textContent = matchesPerSeason;
  if (gamesLeftEl) gamesLeftEl.textContent = gamesLeft;
  if (leagueAgeEl) {
    const { days, hours, minutes } = leagueStats.leagueAge;
    if (days > 0) {
      leagueAgeEl.textContent = `${days}d ${hours}h`;
    } else {
      leagueAgeEl.textContent = `${hours}h ${minutes}m`;
    }
  }

  // Sort teams by points, then net run rate
  const standings = [...state.table].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    return b.runRate - a.runRate;
  });

  // Get current match teams for highlighting
  const currentTeams = [matchState.homeTeam.id, matchState.awayTeam.id];

  // Show all 8 teams
  tableRows.innerHTML = '';
  standings.forEach((row, index) => {
    const team = teams.find(t => t.id === row.teamId);
    const isPlaying = currentTeams.includes(row.teamId);

    const rowEl = document.createElement('div');
    rowEl.className = 'table-row' + (isPlaying ? ' playing' : '');
    rowEl.innerHTML = `
      <span class="row-pos">${index + 1}</span>
      <span class="row-color" style="background:${team ? team.color : '#666'}"></span>
      <span class="row-team">${team ? team.shortName : '???'}</span>
      <span class="row-stat">${row.played}</span>
      <span class="row-stat">${row.won}</span>
      <span class="row-pts">${row.points}</span>
    `;
    tableRows.appendChild(rowEl);
  });

  // Update records
  const topBatterEl = document.getElementById('stat-top-batter');
  const topBowlerEl = document.getElementById('stat-top-bowler');
  const highScoreEl = document.getElementById('stat-high-score');
  const bestBowlingEl = document.getElementById('stat-best-bowling');

  if (topBatterEl && leagueStats.topBatters.length > 0) {
    const top = leagueStats.topBatters[0];
    topBatterEl.innerHTML = `<span class="stat-name">${truncateName(top.name)}</span><span class="stat-value">${top.runs}r</span>`;
  }

  if (topBowlerEl && leagueStats.topBowlers.length > 0) {
    const top = leagueStats.topBowlers[0];
    topBowlerEl.innerHTML = `<span class="stat-name">${truncateName(top.name)}</span><span class="stat-value">${top.wickets}w</span>`;
  }

  if (highScoreEl && leagueStats.highestScore.runs > 0) {
    const hs = leagueStats.highestScore;
    highScoreEl.innerHTML = `<span class="stat-name">High: ${hs.runs}</span><span class="stat-value">${truncateName(hs.player, 8)}</span>`;
  }

  if (bestBowlingEl && leagueStats.bestBowling.wickets > 0) {
    const bb = leagueStats.bestBowling;
    bestBowlingEl.innerHTML = `<span class="stat-name">Best: ${bb.wickets}w</span><span class="stat-value">${truncateName(bb.player, 8)}</span>`;
  }
}

function updateMatchStats(state) {
  // Update team names
  const awayTeamEl = document.getElementById('stat-away-team');
  const homeTeamEl = document.getElementById('stat-home-team');

  if (awayTeamEl) {
    awayTeamEl.textContent = state.awayTeam.name;
    awayTeamEl.style.color = state.awayTeam.color;
  }
  if (homeTeamEl) {
    homeTeamEl.textContent = state.homeTeam.name;
    homeTeamEl.style.color = state.homeTeam.color;
  }

  const batsman1 = document.getElementById('stat-batsman1');
  const batsman2 = document.getElementById('stat-batsman2');
  const bowler = document.getElementById('stat-bowler');
  const runrate = document.getElementById('stat-runrate');
  const required = document.getElementById('stat-required');
  const inningsFours = document.getElementById('stat-innings-fours');
  const inningsSixes = document.getElementById('stat-innings-sixes');
  const lastWicket = document.getElementById('stat-last-wicket');
  const timeLeft = document.getElementById('stat-time-left');
  const progressBar = document.querySelector('#match-progress .progress-fill');

  if (!batsman1 || !state.currentBall) return;

  // Get batsman info
  const striker = state.currentBall.batsman;
  const nonStriker = state.currentBall.nonStriker;
  const bowlerName = state.currentBall.bowler;

  // Calculate run rate
  const currentScore = state.isFirstInnings ? state.firstInningsScore : state.secondInningsScore;
  const overs = currentScore.overs || 0.1;
  const runRate = (currentScore.runs / overs).toFixed(2);

  // Count fours and sixes in recent balls
  let fours = 0;
  let sixes = 0;
  let lastWicketInfo = null;

  state.recentBalls.forEach(ball => {
    if (ball.result === 'FOUR') fours++;
    if (ball.result === 'SIX') sixes++;
    if (ball.result === 'WICKET') {
      lastWicketInfo = ball;
    }
  });

  // Batsmen
  batsman1.innerHTML = `<span class="stat-name">${truncateName(striker)}</span><span class="stat-value">*</span>`;
  batsman1.className = 'stat-row batting strike';

  batsman2.innerHTML = `<span class="stat-name">${truncateName(nonStriker)}</span>`;
  batsman2.className = 'stat-row batting';

  // Bowler
  if (bowler) {
    bowler.innerHTML = `<span class="stat-name">${truncateName(bowlerName)}</span>`;
  }

  // Run rate
  if (runrate) {
    runrate.innerHTML = `<span class="stat-name">Run Rate</span><span class="stat-value">${runRate}</span>`;
  }

  // Required (second innings)
  if (required) {
    if (!state.isFirstInnings && state.target) {
      const runsNeeded = state.target - currentScore.runs;
      const oversLeft = Math.max(0, 20 - overs);
      const reqRate = oversLeft > 0 ? (runsNeeded / oversLeft).toFixed(2) : '-';
      required.innerHTML = `<span class="stat-name">Need ${runsNeeded}</span><span class="stat-value">RR ${reqRate}</span>`;
      required.style.display = 'flex';
    } else {
      required.style.display = 'none';
    }
  }

  // Fours and sixes
  if (inningsFours) {
    inningsFours.innerHTML = `<span class="stat-name">Fours</span><span class="stat-value">${fours}</span>`;
  }
  if (inningsSixes) {
    inningsSixes.innerHTML = `<span class="stat-name">Sixes</span><span class="stat-value">${sixes}</span>`;
  }

  // Last wicket
  if (lastWicket) {
    if (lastWicketInfo) {
      lastWicket.innerHTML = `<span class="stat-name">${truncateName(lastWicketInfo.batsman, 10)}</span>`;
    } else {
      lastWicket.innerHTML = `<span class="stat-name">None yet</span>`;
    }
  }

  // Time remaining
  if (timeLeft) {
    const mins = Math.floor(state.timeRemaining / 60000);
    const secs = Math.floor((state.timeRemaining % 60000) / 1000);
    timeLeft.innerHTML = `<span class="stat-name">Time left</span><span class="stat-value">${mins}:${secs.toString().padStart(2, '0')}</span>`;
  }

  // Progress bar
  if (progressBar) {
    progressBar.style.width = `${state.matchProgress * 100}%`;
  }
}

function truncateName(name, maxLen = 12) {
  if (!name) return '???';
  if (name.length <= maxLen) return name;
  return name.substring(0, maxLen - 1) + '…';
}

function typewriterEffect(text) {
  const commentary = document.getElementById('ff-commentary');
  const cursor = document.getElementById('ff-cursor');
  if (!commentary) return;

  // If same text, don't restart
  if (text === typewriterTarget && typewriterIndex >= typewriterTarget.length) return;

  // Clear previous timeout
  if (typewriterTimeout) {
    clearTimeout(typewriterTimeout);
  }

  // Set new target
  typewriterTarget = text;
  typewriterIndex = 0;
  typewriterText = '';

  // Show cursor while typing
  if (cursor) cursor.classList.remove('visible');

  function typeNext() {
    if (typewriterIndex < typewriterTarget.length) {
      typewriterText += typewriterTarget[typewriterIndex];
      commentary.innerHTML = formatCommentary(typewriterText);
      typewriterIndex++;

      // Variable speed - faster for spaces, slower for punctuation
      let delay = TYPEWRITER_SPEED;
      const char = typewriterTarget[typewriterIndex - 1];
      if (char === ' ') delay = TYPEWRITER_SPEED / 2;
      if (char === '.' || char === '!' || char === '?') delay = TYPEWRITER_SPEED * 4;
      if (char === ',') delay = TYPEWRITER_SPEED * 2;

      typewriterTimeout = setTimeout(typeNext, delay);
    } else {
      // Done typing - show cursor
      if (cursor) cursor.classList.add('visible');
    }
  }

  typeNext();
}

function formatCommentary(text) {
  // Add colored spans for special words
  return text
    .replace(/WICKET!?/g, '<span class="event-wicket">WICKET!</span>')
    .replace(/SIX!?/g, '<span class="event-six">SIX!</span>')
    .replace(/FOUR!?/g, '<span class="event-four">FOUR!</span>')
    .replace(/OUT!?/g, '<span class="event-wicket">OUT!</span>');
}

function updateTicker(ball) {
  let text = `${ball.batsman} vs ${ball.bowler}: ${ball.commentary}`;

  if (ball.result === 'WICKET') {
    text = `WICKET! ${ball.batsman} is OUT! ${ball.commentary}`;
  } else if (ball.result === 'SIX') {
    text = `SIX! ${ball.batsman} sends it into the crowd! ${ball.commentary}`;
  } else if (ball.result === 'FOUR') {
    text = `FOUR! ${ball.batsman} finds the boundary! ${ball.commentary}`;
  }

  // Use typewriter effect instead of direct text
  typewriterEffect(text);
}

function updateThisOver(balls) {
  const overBalls = document.getElementById('nes-over-balls');
  if (!overBalls) return;

  overBalls.innerHTML = '';

  // Get balls from current over only
  const currentOver = balls.length > 0 ? balls[balls.length - 1].over : 0;
  const thisOverBalls = balls.filter(b => b.over === currentOver);

  thisOverBalls.forEach(ball => {
    const span = document.createElement('span');
    span.className = 'ball-result';

    if (ball.result === 'WICKET') {
      span.classList.add('wicket');
      span.textContent = 'W';
    } else if (ball.result === 'SIX') {
      span.classList.add('six');
      span.textContent = '6';
    } else if (ball.result === 'FOUR') {
      span.classList.add('four');
      span.textContent = '4';
    } else if (ball.result === 'DOT') {
      span.classList.add('dot');
      span.textContent = '·';
    } else {
      span.classList.add('runs');
      span.textContent = ball.runs;
    }

    overBalls.appendChild(span);
  });
}

function updateScheduleInfo() {
  const scheduleInfoElements = document.querySelectorAll('.schedule-info');
  if (scheduleInfoElements.length === 0) return;

  function tick() {
    if (document.hidden) return;
    const now = new Date();

    scheduleInfoElements.forEach((el) => {
      const startTimeStr = el.dataset.startTime;
      const statusText = el.querySelector('.status-text');
      if (!statusText || !startTimeStr) return;

      const startTime = new Date(startTimeStr);
      const diffMs = startTime - now;

      if (diffMs <= 0) {
        statusText.textContent = 'Starting now...';
        return;
      }

      const totalSeconds = Math.floor(diffMs / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      let text = 'Airs in ';
      if (days > 0) {
        text += days + 'd ' + String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
      } else {
        text += String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
      }

      statusText.textContent = text;
    });
  }

  tick();
  registerPageInterval(setInterval(tick, 1000));
}

//------------------
// Prediction System
//------------------

function initPredictPanel() {
  const predictBtns = document.querySelectorAll('.predict-btn');
  predictBtns.forEach(btn => {
    btn.addEventListener('click', function() {
      const prediction = this.dataset.predict;
      setPrediction(prediction);
    });
  });

  // Update display
  updatePredictDisplay();
}

function setPrediction(prediction) {
  // Remove active class from all buttons
  document.querySelectorAll('.predict-btn').forEach(btn => {
    btn.classList.remove('active');
  });

  // Set active on selected
  const activeBtn = document.querySelector(`.predict-btn[data-predict="${prediction}"]`);
  if (activeBtn) {
    activeBtn.classList.add('active');
  }

  currentPrediction = prediction;

  // Record which ball we predicted for
  const state = getCurrentMatchState();
  if (state && state.currentBall) {
    predictedBallIndex = state.currentBall.over * 6 + state.currentBall.ball;
  }
}

function checkPrediction(ball) {
  if (!currentPrediction) return;

  const ballIndex = ball.over * 6 + ball.ball;

  // Only check if this is a new ball after we made the prediction
  if (ballIndex <= predictedBallIndex) return;

  let correct = false;
  const result = ball.result;

  switch (currentPrediction) {
    case 'dot':
      correct = result === 'DOT';
      break;
    case 'runs':
      correct = ball.runs >= 1 && ball.runs <= 3 && result !== 'FOUR' && result !== 'SIX' && result !== 'WICKET';
      break;
    case 'four':
      correct = result === 'FOUR';
      break;
    case 'six':
      correct = result === 'SIX';
      break;
    case 'wicket':
      correct = result === 'WICKET';
      break;
  }

  predictionsTotal++;

  if (correct) {
    predictionsCorrect++;
    userStats.predictionsCorrect++;

    // Flash the score green
    const scoreEl = document.querySelector('.predict-score');
    if (scoreEl) {
      scoreEl.classList.add('correct');
      setTimeout(() => scoreEl.classList.remove('correct'), 500);
    }

    // Check prediction achievements
    checkAchievement('predicted10', userStats.predictionsCorrect >= 10);
    checkAchievement('predicted50', userStats.predictionsCorrect >= 50);
  }

  updatePredictDisplay();
  saveUserStats();

  // Reset prediction
  currentPrediction = null;
  document.querySelectorAll('.predict-btn').forEach(btn => {
    btn.classList.remove('active');
  });
}

function updatePredictDisplay() {
  const correctEl = document.getElementById('predict-correct');
  const totalEl = document.getElementById('predict-total');

  if (correctEl) correctEl.textContent = predictionsCorrect;
  if (totalEl) totalEl.textContent = predictionsTotal;
}

//------------------
// Achievement System
//------------------

function loadUserStats() {
  try {
    const saved = localStorage.getItem('cricketUserStats');
    if (saved) {
      userStats = { ...userStats, ...JSON.parse(saved) };
    }

    const savedPredictions = localStorage.getItem('cricketPredictions');
    if (savedPredictions) {
      const p = JSON.parse(savedPredictions);
      predictionsCorrect = p.correct || 0;
      predictionsTotal = p.total || 0;
    }
  } catch (e) {
    console.warn('Could not load user stats:', e);
  }
}

function saveUserStats() {
  try {
    localStorage.setItem('cricketUserStats', JSON.stringify(userStats));
    localStorage.setItem('cricketPredictions', JSON.stringify({
      correct: predictionsCorrect,
      total: predictionsTotal
    }));
  } catch (e) {
    console.warn('Could not save user stats:', e);
  }
}

function getUnlockedAchievements() {
  try {
    const saved = localStorage.getItem('cricketAchievements');
    return saved ? JSON.parse(saved) : [];
  } catch (e) {
    return [];
  }
}

function unlockAchievement(achievementId) {
  const unlocked = getUnlockedAchievements();
  if (unlocked.includes(achievementId)) return;

  unlocked.push(achievementId);

  try {
    localStorage.setItem('cricketAchievements', JSON.stringify(unlocked));
  } catch (e) {
    console.warn('Could not save achievement:', e);
  }

  // Show toast
  const achievement = ACHIEVEMENTS[achievementId];
  if (achievement) {
    showAchievementToast(achievement);
  }
}

function checkAchievement(achievementId, condition) {
  if (condition) {
    unlockAchievement(achievementId);
  }
}

function showAchievementToast(achievement) {
  const toast = document.getElementById('achievement-toast');
  const textEl = document.getElementById('achievement-text');
  const iconEl = toast?.querySelector('.achievement-icon');

  if (!toast || !textEl) return;

  if (iconEl) iconEl.textContent = achievement.icon;
  textEl.textContent = achievement.name;

  toast.classList.add('visible');

  setTimeout(() => {
    toast.classList.remove('visible');
  }, 3000);
}

function trackBallWatched(ball) {
  userStats.ballsWatched++;

  // Track specific events
  if (ball.result === 'SIX') {
    userStats.sixesWatched++;
    checkAchievement('sawSix', true);
  }

  if (ball.result === 'WICKET') {
    userStats.wicketsWatched++;
    userStats.consecutiveWickets++;
    checkAchievement('sawWicket', true);
    checkAchievement('saw10Wickets', userStats.wicketsWatched >= 10);

    // Check for hat-trick (3 wickets in a row)
    if (userStats.consecutiveWickets >= 3) {
      checkAchievement('sawHatTrick', true);
    }
  } else {
    userStats.consecutiveWickets = 0;
  }

  // Check ball watching achievements
  checkAchievement('watched100', userStats.ballsWatched >= 100);
  checkAchievement('watched500', userStats.ballsWatched >= 500);

  // Track for wagon wheel
  if (ball.runs > 0 || ball.result === 'FOUR' || ball.result === 'SIX') {
    wagonWheelData.push({
      direction: ball.shotDirection || 'cover',
      runs: ball.result === 'FOUR' ? 4 : ball.result === 'SIX' ? 6 : ball.runs,
      result: ball.result
    });
  }

  // Track for manhattan
  const over = ball.over;
  if (!manhattanData[over]) {
    manhattanData[over] = 0;
  }
  manhattanData[over] += ball.result === 'FOUR' ? 4 : ball.result === 'SIX' ? 6 : ball.runs;

  saveUserStats();
}

//------------------
// Stats Overlay (Wagon Wheel, Manhattan, H2H)
//------------------

function initStatsOverlay() {
  const toggleBtn = document.getElementById('stats-toggle');
  const overlay = document.getElementById('stats-overlay');
  const closeBtn = overlay?.querySelector('.stats-close');
  const tabs = overlay?.querySelectorAll('.stats-tab');

  if (toggleBtn) {
    toggleBtn.addEventListener('click', () => {
      statsOverlayVisible = !statsOverlayVisible;
      overlay?.classList.toggle('visible', statsOverlayVisible);
      if (statsOverlayVisible) {
        drawStatsCanvas();
      }
    });
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      statsOverlayVisible = false;
      overlay?.classList.remove('visible');
    });
  }

  tabs?.forEach(tab => {
    tab.addEventListener('click', function() {
      tabs.forEach(t => t.classList.remove('active'));
      this.classList.add('active');
      currentStatsTab = this.dataset.tab;
      drawStatsCanvas();
    });
  });
}

function drawStatsCanvas() {
  const canvas = document.getElementById('stats-canvas');
  const info = document.getElementById('stats-info');
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  switch (currentStatsTab) {
    case 'wagon':
      drawWagonWheel(ctx, canvas);
      if (info) info.innerHTML = `<span class="cyan">Total scoring shots:</span> ${wagonWheelData.length}`;
      break;
    case 'manhattan':
      drawManhattan(ctx, canvas);
      if (info) info.innerHTML = `<span class="cyan">Overs bowled:</span> ${manhattanData.filter(x => x !== undefined).length}`;
      break;
    case 'h2h':
      drawHeadToHead(ctx, canvas);
      break;
  }
}

function drawWagonWheel(ctx, canvas) {
  const cx = canvas.width / 2;
  const cy = canvas.height / 2 + 20;
  const radius = 80;

  // Draw pitch outline
  ctx.strokeStyle = '#555';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();

  // Draw center pitch
  ctx.fillStyle = '#8b7355';
  ctx.fillRect(cx - 10, cy - 30, 20, 60);

  // Direction map (angles in radians, 0 = right, going clockwise)
  const directionAngles = {
    cover: -Math.PI / 4,
    midOff: -Math.PI / 6,
    midOn: Math.PI / 6,
    midWicket: Math.PI / 4,
    squareLeg: Math.PI / 2,
    point: -Math.PI / 2,
    thirdMan: -Math.PI * 3/4,
    fineLeg: Math.PI * 3/4,
    longOn: Math.PI / 5,
    longOff: -Math.PI / 5,
    slip: -Math.PI * 5/6,
    gully: -Math.PI * 2/3,
  };

  // Draw scoring shots
  wagonWheelData.slice(-50).forEach((shot, i) => {
    const angle = directionAngles[shot.direction] || 0;
    const dist = (shot.runs / 6) * radius * 0.9 + 20;

    const endX = cx + Math.cos(angle) * dist;
    const endY = cy + Math.sin(angle) * dist;

    // Color based on runs
    if (shot.result === 'SIX') {
      ctx.strokeStyle = '#f8d800';
    } else if (shot.result === 'FOUR') {
      ctx.strokeStyle = '#0058f8';
    } else {
      ctx.strokeStyle = '#00ff00';
    }

    ctx.lineWidth = shot.runs >= 4 ? 2 : 1;
    ctx.globalAlpha = 0.5 + (i / 100);

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(endX, endY);
    ctx.stroke();
  });

  ctx.globalAlpha = 1;

  // Legend
  ctx.font = '10px monospace';
  ctx.fillStyle = '#f8d800';
  ctx.fillText('6', 10, 15);
  ctx.fillStyle = '#0058f8';
  ctx.fillText('4', 30, 15);
  ctx.fillStyle = '#00ff00';
  ctx.fillText('1-3', 50, 15);
}

function drawManhattan(ctx, canvas) {
  const barWidth = 12;
  const maxHeight = canvas.height - 40;
  const baseY = canvas.height - 20;

  // Find max runs in an over
  const maxRuns = Math.max(20, ...manhattanData.filter(x => x !== undefined));

  // Draw bars
  manhattanData.forEach((runs, over) => {
    if (runs === undefined) return;

    const x = 20 + over * (barWidth + 4);
    const barHeight = (runs / maxRuns) * maxHeight;

    // Color based on runs
    if (runs >= 15) {
      ctx.fillStyle = '#f8d800'; // Excellent over
    } else if (runs >= 10) {
      ctx.fillStyle = '#00ff00'; // Good over
    } else if (runs >= 6) {
      ctx.fillStyle = '#00ffff'; // Average
    } else {
      ctx.fillStyle = '#ff6666'; // Low scoring
    }

    ctx.fillRect(x, baseY - barHeight, barWidth, barHeight);

    // Over number
    ctx.fillStyle = '#888';
    ctx.font = '8px monospace';
    ctx.fillText(over + 1, x + 2, baseY + 10);
  });

  // Y-axis labels
  ctx.fillStyle = '#888';
  ctx.font = '9px monospace';
  ctx.fillText('0', 5, baseY);
  ctx.fillText(Math.floor(maxRuns / 2).toString(), 5, baseY - maxHeight / 2);
  ctx.fillText(maxRuns.toString(), 5, baseY - maxHeight + 10);
}

function drawHeadToHead(ctx, canvas) {
  const state = getCurrentMatchState();
  if (!state) return;

  const info = document.getElementById('stats-info');

  // Get H2H key
  const team1 = state.homeTeam.id;
  const team2 = state.awayTeam.id;
  const key = [team1, team2].sort().join('-');

  // Load H2H data
  let h2h = { matches: 0, team1Wins: 0, team2Wins: 0, draws: 0 };
  try {
    const saved = localStorage.getItem('cricketH2H');
    if (saved) {
      const allH2H = JSON.parse(saved);
      h2h = allH2H[key] || h2h;
    }
  } catch (e) {}

  // Draw H2H stats
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('HEAD TO HEAD', canvas.width / 2, 25);

  ctx.font = '12px monospace';
  ctx.fillStyle = state.homeTeam.color;
  ctx.fillText(state.homeTeam.name, canvas.width / 2, 55);

  ctx.fillStyle = '#888';
  ctx.fillText('vs', canvas.width / 2, 75);

  ctx.fillStyle = state.awayTeam.color;
  ctx.fillText(state.awayTeam.name, canvas.width / 2, 95);

  // Stats
  ctx.fillStyle = '#ffffff';
  ctx.font = '11px monospace';
  ctx.textAlign = 'left';
  ctx.fillText(`Matches: ${h2h.matches}`, 30, 130);
  ctx.fillText(`${state.homeTeam.shortName} wins: ${h2h.team1Wins}`, 30, 150);
  ctx.fillText(`${state.awayTeam.shortName} wins: ${h2h.team2Wins}`, 30, 170);

  if (info) {
    info.innerHTML = h2h.matches > 0 ?
      `<span class="yellow">Last ${h2h.matches} meetings</span>` :
      `<span class="cyan">First meeting!</span>`;
  }
}

//------------------
// Logo Entrance Animation
//------------------

function animateLogo(isMenuPage) {
  var splash = document.getElementById('site-splash');
  var els = document.querySelectorAll('.site-logo .start');

  // Returning from menu close — skip splash entirely
  if (sessionStorage.getItem('skipSplash')) {
    sessionStorage.removeItem('skipSplash');
    els.forEach(function (el) { el.classList.remove('start'); });
    if (splash) splash.classList.add('done');
    return;
  }

  // Animate letters in (works for both splash and menu-page positions)
  var totalDelay = els.length * 100;

  for (var a = 0; a < els.length; a++) {
    (function (el, delay) {
      setTimeout(function () {
        el.classList.remove('start');
      }, delay * 100);
    })(els[a], a);
  }

  // On menu pages, keep the logo visible (it sits in the top-left of the menu)
  // On normal pages, fade out the splash after the animation
  if (!isMenuPage && splash) {
    setTimeout(function () {
      splash.classList.add('done');
    }, totalDelay + 1200);
  }
}

//------------------
// Menu Toggle
//------------------

function bindMenu() {
  const toggle = document.getElementById('menu-toggle');
  const overlay = document.getElementById('menu-overlay');
  if (!toggle || !overlay) return false;

  const panel = overlay.querySelector('.menu-panel');
  const contentPanel = document.getElementById('menu-content');
  const contentInner = overlay.querySelector('.menu-panel__content-inner');
  const backBtn = document.getElementById('menu-back');
  const backLabel = document.getElementById('menu-back-label');
  const rows = overlay.querySelectorAll('.menu-panel__row');
  // Tracks whether the menu was auto-opened on a direct page visit
  let isMenuPage = false;

  function openMenu() {
    overlay.classList.add('active');
    document.body.classList.add('menu-open', 'no-scroll');
    toggle.setAttribute('aria-expanded', 'true');
    overlay.setAttribute('aria-hidden', 'false');
    toggle.setAttribute('aria-label', 'Close menu');
  }

  function resetPanel() {
    if (panel) panel.classList.remove('has-content');
    rows.forEach(function (r) { r.classList.remove('selected'); });
    if (contentInner) contentInner.innerHTML = '';
  }

  function closeMenu() {
    overlay.classList.remove('active');
    document.body.classList.remove('menu-open', 'no-scroll');
    toggle.setAttribute('aria-expanded', 'false');
    overlay.setAttribute('aria-hidden', 'true');
    toggle.setAttribute('aria-label', 'Open menu');

    // If we're on a menu page (direct visit or navigated within menu),
    // fetch the homepage content and swap it in without a full reload.
    if (isMenuPage) {
      var splash = document.getElementById('site-splash');
      if (splash) splash.classList.add('done');
      document.body.classList.remove('menu-page');

      fetch('/', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) { return r.text(); })
        .then(function (data) {
          // Homepage uses the full layout (not bare), so extract #main-content
          var temp = document.createElement('div');
          temp.innerHTML = data;
          var main = temp.querySelector('#main-content');
          var mainContent = document.querySelector('#main-content');
          if (main && mainContent) {
            mainContent.innerHTML = main.innerHTML;
          }

          // Show footer and restore page
          var footer = document.querySelector('footer');
          if (footer) footer.style.display = '';

          window.history.pushState({}, '', '/');
          isMenuPage = false;
          resetPanel();
          pageLoad();
        })
        .catch(function () {
          // Fallback to hard navigation if fetch fails
          window.location.href = '/';
        });
      return;
    }

    // Normal close — just reset panel
    setTimeout(resetPanel, 350);
  }

  function selectRow(row) {
    rows.forEach(function (r) { r.classList.remove('selected'); });
    row.classList.add('selected');
    var label = row.querySelector('.menu-panel__label');
    if (backLabel && label) {
      backLabel.textContent = label.textContent;
    }
  }

  function loadMenuContent(url, row) {
    selectRow(row);

    if (contentInner) contentInner.classList.add('loading');
    if (contentPanel) contentPanel.scrollTop = 0;

    fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.text(); })
      .then(function (data) {
        if (contentInner) {
          contentInner.innerHTML = data;
          contentInner.classList.remove('loading');
        }
        if (panel) panel.classList.add('has-content');
      })
      .catch(function (err) {
        console.error('Menu fetch error:', err);
        if (contentInner) contentInner.classList.remove('loading');
      });
  }

  // --- Close active app (not the player sidebar) ---
  function closeActiveApp() {
    // Check for full-screen app
    var appBody = document.querySelector('.app-body');
    if (appBody) {
      appBody.style.opacity = 0;
      // Reset any dimmed app icons
      document.querySelectorAll('.app').forEach(function(a) { a.style.opacity = ''; });
      setTimeout(function () {
        if (appBody.parentNode) {
          appBody.parentNode.removeChild(appBody);
        }
        updateToggleIcon();
      }, 300);
      window.history.pushState({}, '', '/');
      return true;
    }

    return false;
  }

  // --- Toggle button ---
  toggle.addEventListener('click', function () {
    if (overlay.classList.contains('active')) {
      closeMenu();
    } else if (!closeActiveApp()) {
      openMenu();
      // Desktop: load first item if nothing is selected yet
      // Mobile: just show the numbered list
      if (window.innerWidth >= 640 && !overlay.querySelector('.menu-panel__row.selected') && rows.length) {
        loadMenuContent(rows[0].getAttribute('href'), rows[0]);
      }
    }
  });

  // --- Keyboard navigation ---
  document.addEventListener('keydown', function (e) {
    if (!overlay.classList.contains('active')) return;

    // Escape closes menu
    if (e.key === 'Escape') {
      closeMenu();
      return;
    }

    // Number keys (1-9) jump to that menu item
    var num = parseInt(e.key, 10);
    if (num >= 1 && num <= rows.length) {
      e.preventDefault();
      var targetRow = rows[num - 1];
      if (targetRow) {
        isMenuPage = true;
        loadMenuContent(targetRow.getAttribute('href'), targetRow);
      }
      return;
    }

    // Arrow keys move between rows
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      var currentIndex = -1;
      rows.forEach(function (r, i) {
        if (r.classList.contains('selected')) currentIndex = i;
      });

      var nextIndex;
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex < rows.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : rows.length - 1;
      }

      var nextRow = rows[nextIndex];
      if (nextRow) {
        isMenuPage = true;
        loadMenuContent(nextRow.getAttribute('href'), nextRow);
      }
    }
  });

  // --- Menu link clicks ---
  rows.forEach(function (link) {
    link.addEventListener('click', function (e) {
      e.preventDefault();
      var href = link.getAttribute('href');
      if (!href) return;

      isMenuPage = true;
      loadMenuContent(href, link);
    });
  });

  // --- Back button (mobile) ---
  if (backBtn) {
    backBtn.addEventListener('click', function () {
      if (panel) panel.classList.remove('has-content');
    });
  }

  // --- Auto-open on direct page visit ---
  // If the current URL matches a menu item (not homepage),
  // open the menu and fetch the content via the same mechanism as clicking.
  var currentPath = window.location.pathname.replace(/\/+$/, '');
  if (currentPath && currentPath !== '') {
    var matchingRow = null;
    rows.forEach(function (row) {
      var href = row.getAttribute('href');
      if (href && href.replace(/\/+$/, '') === currentPath) {
        matchingRow = row;
      }
    });

    if (matchingRow) {
      isMenuPage = true;

      // Clear the page behind the overlay so nothing shows through
      var mainContent = document.querySelector('#main-content');
      if (mainContent) mainContent.innerHTML = '';
      var footer = document.querySelector('footer');
      if (footer) footer.style.display = 'none';

      // Open menu and fetch the correct page content
      openMenu();
      loadMenuContent(matchingRow.getAttribute('href'), matchingRow);
      return true;
    }
  }

  return false;
}

//------------------
// Toggle Icon State
//------------------

function updateToggleIcon() {
  var hasApp = document.querySelector('.sidebar.active') || document.querySelector('.app-body');
  if (hasApp) {
    document.body.classList.add('app-active');
  } else {
    document.body.classList.remove('app-active');
  }
}

//------------------
// App Bar (Swiper)
//------------------

function bindAppbar() {
  new Swiper('.swiper', {
    loop: true,
    slidesPerView: 'auto',
    centeredSlides: true,
    spaceBetween: 12,
    mousewheel: {
      enabled: true,
      invert: true,
    },
    breakpoints: {
      640: {
        centeredSlides: false,
        spaceBetween: 0,
      },
    },
  });

  if (!window.touch) {
    bindCardTilt();
  }
}

function bindCardTilt() {
  const cards = document.querySelectorAll('.app .inner');
  const maxTilt = 8;

  cards.forEach(function (card) {
    const app = card.closest('.app');

    app.addEventListener('mousemove', function (e) {
      const rect = app.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      const rotateY = (x - 0.5) * maxTilt * 2;
      const rotateX = (0.5 - y) * maxTilt * 2;

      card.style.transition = 'transform 0.1s ease-out';
      card.style.transform = 'rotateX(' + rotateX + 'deg) rotateY(' + rotateY + 'deg)';

      // Fixed light source above — reflection angle = 2x tilt angle
      // Band sweeps opposite to tilt, amplified so it flashes across
      var tilt = rotateY / maxTilt; // -1 to 1
      var glossX = 50 - tilt * 80;
      card.style.setProperty('--gloss-x', glossX + '%');
    });

    app.addEventListener('mouseleave', function () {
      card.style.transition = 'transform 0.4s ease-out';
      card.style.transform = '';
      card.style.setProperty('--gloss-x', '120%');
    });
  });
}

//------------------
// Sidebar Controls
//------------------

function initLargeModeControls(sidebar) {
  let hideTimeout = null;

  function showControls() {
    sidebar.classList.add('controls-visible');
    clearTimeout(hideTimeout);
    hideTimeout = setTimeout(function () {
      if (sidebar.classList.contains('large-mode')) {
        sidebar.classList.remove('controls-visible');
      }
    }, 3000);
  }

  function onMouseMove() {
    if (!sidebar.classList.contains('large-mode')) return;
    showControls();
  }

  function onTouchStart() {
    if (!sidebar.classList.contains('large-mode')) return;
    if (sidebar.classList.contains('controls-visible')) {
      sidebar.classList.remove('controls-visible');
      clearTimeout(hideTimeout);
    } else {
      showControls();
    }
  }

  // Clean up any previous listeners
  if (sidebar._largeModeCleanup) {
    sidebar._largeModeCleanup();
  }

  sidebar.addEventListener('mousemove', onMouseMove);
  sidebar.addEventListener('touchstart', onTouchStart);

  sidebar._largeModeCleanup = function () {
    sidebar.removeEventListener('mousemove', onMouseMove);
    sidebar.removeEventListener('touchstart', onTouchStart);
    clearTimeout(hideTimeout);
  };

  // Show controls initially then auto-hide
  showControls();
}

function sidebarState() {
  const min = document.querySelectorAll('.minimise-player');
  const max = document.querySelectorAll('.enlarge-player');
  const restore = document.querySelectorAll('.restore-player');
  const close = document.querySelectorAll('.close-player');

  for (let a = 0; a < min.length; a++) {
    const elm = min[a];
    const sidebar = elm.closest('.sidebar');

    elm.addEventListener('click', function () {
      sidebar.classList.remove('large-mode', 'controls-visible');
      sidebar.classList.add('mini-mode');
      if (sidebar._largeModeCleanup) {
        sidebar._largeModeCleanup();
        sidebar._largeModeCleanup = null;
      }
    });
  }

  for (let a = 0; a < max.length; a++) {
    const elm = max[a];
    const sidebar = elm.closest('.sidebar');

    elm.addEventListener('click', function () {
      sidebar.classList.add('large-mode');
      sidebar.classList.remove('mini-mode');
      initLargeModeControls(sidebar);
    });
  }

  for (let a = 0; a < restore.length; a++) {
    const elm = restore[a];
    const sidebar = elm.closest('.sidebar');

    elm.addEventListener('click', function () {
      sidebar.classList.remove('mini-mode', 'large-mode', 'controls-visible');
      if (sidebar._largeModeCleanup) {
        sidebar._largeModeCleanup();
        sidebar._largeModeCleanup = null;
      }
    });
  }

  for (let a = 0; a < close.length; a++) {
    const elm = close[a];
    const sidebar = elm.closest('.sidebar');

    elm.addEventListener('click', function (e) {
      e.preventDefault();

      window.history.pushState({}, '', '/');
      sidebar.classList.remove('active');

      if (player.elm && player.elm === sidebar) {
        stopPlayer();

        setTimeout(function () {
          if (player.elm && player.elm.parentNode) {
            player.elm.parentNode.removeChild(player.elm);
          }
          resetPlayer();
        }, 400);
      } else {
        setTimeout(function () {
          if (sidebar.parentNode) {
            sidebar.parentNode.removeChild(sidebar);
          }
        }, 400);
      }
    });
  }
}

//------------------
// Navigation Callbacks
//------------------

function loadContent(e) {
  e.preventDefault();

  const elm = this;
  const newUrl = elm.href;

  let type = 'link';
  if (elm.classList.contains('episode') || elm.classList.contains('channel-link')) {
    type = 'episode';
  } else if (elm.classList.contains('app')) {
    type = 'app';
  }

  loadPageWrapper(type, newUrl, elm);
}

function loadPageWrapper(type, path, link) {
  loadPage(type, path, link, player, pageLoad, getSoundBoardPlayer());
}

//------------------
// Schedule Check
//------------------

function runScheduleCheck() {
  // Skip when the tab is in the background — there's nothing to update for
  // an invisible UI, and at scale this saves a lot of needless work.
  if (document.hidden) return;
  checkForActive(player, loadPageWrapper);
}

// Re-check schedule periodically when online. Once every 5s is plenty for
// a TV-style schedule UI; the previous 1s cadence multiplied across thousands
// of concurrent visitors would put real load on the origin if anything in
// checkForActive ever started fetching.
if (!isOffline) {
  setInterval(runScheduleCheck, 5000);
}

//------------------
// Initialize on Load
//------------------

function bootstrap() {
  firstLoad();
  pageLoad();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

setupPopstateHandler();
